import { useState, useMemo } from "react";
import { startRegistration, startAuthentication } from "@simplewebauthn/browser";
import { apiClient } from "../api/client";
import { toast } from "sonner";

interface LoginResult {
  user: {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
    tenantId: string;
    permissions: string[];
    roles: Array<{ id: string; name: string; displayName: string }>;
    passkeyPromptDismissed: boolean;
  };
  tenant: { id: string; name: string; slug: string };
  accessToken: string;
  refreshToken: string;
  expiresAt: string;
}

export function usePasskey() {
  const [isLoading, setIsLoading] = useState(false);

  const isSupported = useMemo(() => {
    return (
      typeof window !== "undefined" &&
      !!window.PublicKeyCredential &&
      !!navigator.credentials
    );
  }, []);

  const registerPasskey = async (friendlyName?: string): Promise<void> => {
    setIsLoading(true);
    try {
      const options = await apiClient.post<any>("/auth/passkey/register/options", {});
      const credential = await startRegistration({ optionsJSON: options });
      await apiClient.post("/auth/passkey/register/verify", {
        credential,
        friendlyName,
      });
      toast.success("Passkey enregistré avec succès !");
    } catch (err: any) {
      if (err?.name === "NotAllowedError") {
        toast.error("Opération annulée.");
      } else if (err?.name === "SecurityError") {
        toast.error("Erreur de sécurité. Vérifiez que vous êtes sur le bon domaine.");
      } else {
        toast.error(err?.message || "Erreur lors de l'enregistrement du passkey.");
      }
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const loginWithPasskey = async (email?: string): Promise<LoginResult> => {
    setIsLoading(true);
    try {
      const options = await apiClient.post<any>("/auth/passkey/login/options", {
        email,
      });
      const credential = await startAuthentication({ optionsJSON: options });
      const result = await apiClient.post<LoginResult>(
        "/auth/passkey/login/verify",
        { credential }
      );
      return result;
    } catch (err: any) {
      if (err?.name === "NotAllowedError") {
        toast.error("Opération annulée.");
      } else if (err?.name === "SecurityError") {
        toast.error("Erreur de sécurité.");
      } else {
        toast.error(err?.message || "Échec de la connexion par passkey.");
      }
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  return { isSupported, isLoading, registerPasskey, loginWithPasskey };
}
